import React from 'react';
import {
  getVariableEntries,
  serializeIx2Data,
  styleBlocksToClassMap,
} from '@packages/systems/ix2/shared-utils';
import {IX2EngineConstants} from '@packages/systems/ix2/shared-constants';
import {ProjectDataToStore} from '@packages/systems/designer/canvas';
import {emptyVariableCollectionMap} from '@packages/systems/core/utils/StyleVariables';

/**
 * Based on the data received from Designer App, initialize interactions (IX2) on the page. It will initialize interactions only once per page loaded,
 * so if we expect this to support realtime update for IX2 in the future we need to change logic here.
 */
export const useInitializeIX2OnPage = ({
  ix2Events,
  ix2ActionLists,
  styleBlocks,
  slugMap,
  variables,
  variableCollections,
  pageId,
  isLoading,
}: {
  ix2Events: ProjectDataToStore['ix2Events'];
  ix2ActionLists: ProjectDataToStore['ix2ActionLists'];
  styleBlocks: ProjectDataToStore['styleBlockState']['styleBlocks'] | undefined;
  slugMap: ProjectDataToStore['slugMap'] | undefined;
  variables: ProjectDataToStore['cssVariablesState']['variables'] | undefined;
  variableCollections:
    | ProjectDataToStore['cssVariablesState']['variableCollections']
    | undefined;
  pageId: ProjectDataToStore['pageId'] | undefined;
  isLoading: boolean;
}) => {
  const ix2IsInitializedForPage = React.useRef<string | null>(null);

  // update attribute on the document that stores id of the current page as it is used in the IX2 code that is part of the user projects
  React.useEffect(() => {
    if (pageId) {
      document.documentElement.setAttribute(IX2EngineConstants.WF_PAGE, pageId);
    }
  }, [pageId]);

  // initialize interactions on the page
  React.useLayoutEffect(() => {
    if (
      // wait till the Webflow owned JS code (available under Webflow namespace) has been initialized, so we can access interaction initializations
      window.Webflow &&
      window.Webflow.require &&
      // wait for all data needed for interactions initializations
      ix2Events &&
      ix2ActionLists &&
      styleBlocks &&
      slugMap &&
      variables &&
      pageId &&
      !isLoading // page is still loading (wait for complete data)
    ) {
      if (ix2IsInitializedForPage.current === pageId) {
        /* Skip initialization if either:
         * - the page is the same (we already initialized interactions for it)
         */
        return;
      }

      // transform IX2 into the expected format
      const rawIX2Data = serializeIx2Data({
        events: ix2Events,
        actionLists: ix2ActionLists,
        classMap: styleBlocksToClassMap({
          styleBlocks,
          slugMap,
        }),
        variableEntries: getVariableEntries(
          variables,
          variableCollections ?? emptyVariableCollectionMap,
          slugMap
        ),
      });

      // initialize interactions in the same way we do it on published sites
      window.Webflow.require('ix2').init(rawIX2Data);

      // similarly to when we switch to regular preview we dispatch this custom event to indicate ti IX2 engine that page has changed
      document.dispatchEvent(new CustomEvent('IX2_PAGE_UPDATE'));

      // storying the pageId for which we initialized interactions, so we don't do it again
      ix2IsInitializedForPage.current = pageId;
    }
  }, [
    ix2Events,
    ix2ActionLists,
    styleBlocks,
    slugMap,
    variables,
    variableCollections,
    pageId,
    isLoading,
  ]);
};
